import React, { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import _get from "lodash.get";

interface OverrideWithReasonFieldProps {
  /** When true, always show the reason textarea (failed state) */
  alwaysShowReason?: boolean;
  children: React.ReactNode;
  extractedValue: unknown;
  fieldName: string;
  label: string;
}

export const OverrideWithReasonField: React.FC<OverrideWithReasonFieldProps> = ({
  fieldName,
  extractedValue,
  label,
  alwaysShowReason = false,
  children,
}) => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<Record<string, unknown>>();

  const fieldValue = _get(values, fieldName);
  const overrideKey = `overrides.${fieldName}`;
  const overrideValue = (_get(values, overrideKey) as string) || "";

  // If there's no extracted value (OCR failed or no data), never show override reason
  const hasExtractedValue = extractedValue !== undefined && extractedValue !== null && extractedValue !== "";

  // Normalize values for comparison — Date objects need to be compared as YYYY-MM-DD strings,
  // and formatted numbers (with commas) need commas stripped before comparing.
  const normalizeForCompare = (val: unknown): string => {
    if (val instanceof Date) {
      const yyyy = val.getFullYear();
      const mm = String(val.getMonth() + 1).padStart(2, "0");
      const dd = String(val.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    if (typeof val === "string") {
      return val.replace(/,/g, "");
    }
    return String(val ?? "");
  };

  const isDiverged = hasExtractedValue && normalizeForCompare(fieldValue) !== normalizeForCompare(extractedValue);
  const showReason = alwaysShowReason || isDiverged;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevShowReason = useRef(showReason);

  useEffect(() => {
    if (showReason && !prevShowReason.current && textareaRef.current) {
      textareaRef.current.focus();
    }
    prevShowReason.current = showReason;
  }, [showReason]);

  useEffect(() => {
    if (!showReason && overrideValue !== "") {
      setFieldValue(overrideKey, "");
    }
  }, [showReason, overrideKey, overrideValue, setFieldValue]);

  const reasonError =
    _get(touched, overrideKey) && (_get(errors, overrideKey) as string);

  return (
    <div data-testid={`override-field-${fieldName}`}>
      {children}

      <div aria-live="polite">
        {showReason && (
          <div className="mt-2">
            <label
              htmlFor={`override-reason-${fieldName}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {`Reason for changing "${label}"`}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              id={`override-reason-${fieldName}`}
              ref={textareaRef}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              value={overrideValue}
              onChange={(e) => setFieldValue(overrideKey, e.target.value)}
              data-testid={`override-reason-${fieldName}`}
              aria-required="true"
              aria-describedby={
                reasonError ? `override-reason-error-${fieldName}` : undefined
              }
            />
            {reasonError && (
              <p
                id={`override-reason-error-${fieldName}`}
                className="text-red-600 text-xs mt-1"
                role="alert"
              >
                {reasonError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
